import React, { useEffect, useState } from "react";
import ninaMarketState from "../data/ninaMarketState";
import { validateScannerReviewHandoff } from "../scanner/NinaScannerReviewHandoff";
import "./NinaTradePlan.css";

function NinaTradePlan({ demoMarketState, scannerReview = null }) {
  const { currentTrade, ninaVerdict } = ninaMarketState;
  const scannerValidation = scannerReview
    ? validateScannerReviewHandoff(scannerReview)
    : { valid: false, value: null, error: "" };
  const validReview = scannerValidation.valid ? scannerValidation.value : null;

  const [selectedSymbol, setSelectedSymbol] = useState(() => {
    try {
      return (
        localStorage.getItem("aninaSelectedSymbol") ||
        currentTrade.symbol ||
        "PLUG"
      );
    } catch (error) {
      return currentTrade.symbol || "PLUG";
    }
  });

  useEffect(() => {
    const handleSymbolChange = (event) => {
      const nextSymbol = event.detail?.symbol;

      if (!nextSymbol) {
        return;
      }

      setSelectedSymbol(nextSymbol);
    };

    window.addEventListener(
      "nina:symbol-change",
      handleSymbolChange
    );

    return () => {
      window.removeEventListener(
        "nina:symbol-change",
        handleSymbolChange
      );
    };
  }, []);

  const isDemoActive = Boolean(demoMarketState);

  const basePrice = isDemoActive
    ? demoMarketState.price
    : currentTrade.currentPrice;

  const entry = isDemoActive
    ? basePrice + 0.03
    : currentTrade.entry;

  const stop = isDemoActive
    ? basePrice - 0.1
    : currentTrade.stop;

  const target1 = isDemoActive
    ? basePrice + 0.2
    : currentTrade.targets.target1;

  const target2 = isDemoActive
    ? basePrice + 0.4
    : currentTrade.targets.target2;

  const target3 = isDemoActive
    ? basePrice + 0.6
    : currentTrade.targets.target3;

  const symbol = validReview?.symbol || (isDemoActive
    ? demoMarketState.symbol
    : selectedSymbol);

  const decision = isDemoActive
    ? demoMarketState.decision
    : ninaVerdict.decision;

  const nextAction = isDemoActive
    ? demoMarketState.nextAction
    : ninaVerdict.tradePermission;

  const riskLevel = isDemoActive
    ? demoMarketState.riskLevel
    : "Low";

  const tradeBlocked =
    decision === "Risk Too High" ||
    decision === "Do Not Enter" ||
    riskLevel === "High" ||
    riskLevel === "Very High";

  const riskPerShare = Math.abs(entry - stop);
  const targetOneReward = target1 - entry;
  const targetTwoReward = target2 - entry;
  const targetThreeReward = target3 - entry;

  const riskReward =
    riskPerShare > 0
      ? targetTwoReward / riskPerShare
      : 0;

  const getPlanStatus = () => {
    if (tradeBlocked) {
      return "TRADE BLOCKED";
    }

    if (
      decision === "Entry Building" ||
      decision === "Watch Breakout"
    ) {
      return "SETUP DEVELOPING";
    }

    return "WAITING FOR CONFIRMATION";
  };

  const getPlanClassName = () => {
    if (tradeBlocked) {
      return "tradePlanBlocked";
    }

    if (
      decision === "Entry Building" ||
      decision === "Watch Breakout"
    ) {
      return "tradePlanDeveloping";
    }

    return "tradePlanWaiting";
  };

  return (
    <section className="ninaTradePlanCard">
      {scannerReview && !validReview && (
        <div className="scannerReviewError" role="alert">
          {scannerValidation.error}
        </div>
      )}
      {validReview && (
        <section className="scannerReviewHandoff" aria-labelledby="scanner-review-heading">
          <span>SCANNER REVIEW HANDOFF · SIMULATED</span>
          <h3 id="scanner-review-heading">
            {validReview.kind === "options"
              ? `${validReview.symbol} ${validReview.expiration} ${validReview.strike} ${validReview.contractType}`
              : `${validReview.symbol} · ${validReview.setup}`}
          </h3>
          <p><strong>Review shortcut only — no order has been placed.</strong></p>
          <p>
            This browser-only handoff is for independent planning. It does not
            connect to a brokerage, submit an order, confirm a trade, or write to
            AI Memory.
          </p>
          {validReview.kind === "options" && (
            <dl className="scannerReviewFacts">
              <ReviewFact label="Underlying" value={validReview.symbol} />
              <ReviewFact label="Contract ID" value={validReview.contractId} />
              <ReviewFact label="Type" value={validReview.contractType} />
              <ReviewFact label="Direction" value={validReview.direction} />
              <ReviewFact label="Strategy/setup" value={validReview.setup} />
              <ReviewFact label="Expiration" value={validReview.expiration} />
              <ReviewFact label="DTE" value={validReview.dte} />
              <ReviewFact label="Strike" value={formatReviewNumber(validReview.strike)} />
              <ReviewFact label="Bid" value={formatReviewNumber(validReview.bid)} />
              <ReviewFact label="Ask" value={formatReviewNumber(validReview.ask)} />
              <ReviewFact label="Spread" value={formatReviewNumber(validReview.spread)} />
              <ReviewFact label="Multiplier" value={validReview.contractMultiplier} />
              <ReviewFact label="Premium/contract" value={formatReviewNumber(validReview.premium)} />
              <ReviewFact label="Maximum-loss basis" value={`${formatReviewNumber(validReview.maxLoss)} (ask × validated multiplier)`} />
              <ReviewFact label="Risk" value={validReview.riskLevel} />
              <ReviewFact label="AI SmartFlow" value={validReview.smartFlow} />
              <ReviewFact label="AI Confidence" value={validReview.confidence} />
              <ReviewFact label="Evidence status" value={validReview.evidenceStatus} />
              <ReviewFact label="Evidence completeness" value={`${validReview.evidenceCompleteness}%`} />
              <ReviewFact label="Source/provenance" value={validReview.source} />
              <ReviewFact label="As of" value={validReview.asOf} />
              <ReviewFact label="Status" value="Simulated" />
            </dl>
          )}
          {validReview.kind !== "options" && (
            <dl className="scannerReviewFacts">
              <ReviewFact label="Scanner mode" value={validReview.kind} />
              <ReviewFact label="Symbol" value={validReview.symbol} />
              <ReviewFact label="Direction" value={validReview.direction} />
              <ReviewFact label="Setup" value={validReview.setup} />
              <ReviewFact label="Risk" value={validReview.riskLevel} />
              <ReviewFact label="AI SmartFlow" value={validReview.smartFlow} />
              <ReviewFact label="AI Confidence" value={validReview.confidence} />
              <ReviewFact label="Evidence status" value={validReview.evidenceStatus} />
              <ReviewFact label="Evidence completeness" value={`${validReview.evidenceCompleteness}%`} />
              <ReviewFact label="Source/provenance" value={validReview.source} />
              <ReviewFact label="As of" value={validReview.asOf} />
              <ReviewFact label="Status" value="Simulated" />
            </dl>
          )}
        </section>
      )}
      {!validReview && (
        <>
      <div className="ninaTradePlanHeader">
        <div>
          <span className="tradePlanEyebrow">
            AI EXECUTION FRAMEWORK
          </span>

          <h2>🎯 AI Trade Plan</h2>

          <p>
            {symbol} • Entry, stop, targets, and trade
            permission
          </p>
        </div>

        <div
          className={`tradePlanStatus ${getPlanClassName()}`}
        >
          {getPlanStatus()}
        </div>
      </div>

      <div className="tradePlanLevelsGrid">
        <div className="tradePlanLevel tradePlanEntry">
          <span>Entry</span>

          <strong>
            {tradeBlocked
              ? "BLOCKED"
              : formatPrice(entry)}
          </strong>

          <small>Confirmation required</small>
        </div>

        <div className="tradePlanLevel tradePlanStop">
          <span>Stop Loss</span>

          <strong>{formatPrice(stop)}</strong>

          <small>
            {formatPrice(riskPerShare)} risk per share
          </small>
        </div>

        <div className="tradePlanLevel tradePlanTarget">
          <span>Target 1</span>

          <strong>{formatPrice(target1)}</strong>

          <small>
            +{formatPrice(targetOneReward)}
          </small>
        </div>

        <div className="tradePlanLevel tradePlanTarget">
          <span>Target 2</span>

          <strong>{formatPrice(target2)}</strong>

          <small>
            +{formatPrice(targetTwoReward)}
          </small>
        </div>

        <div className="tradePlanLevel tradePlanTarget">
          <span>Target 3</span>

          <strong>{formatPrice(target3)}</strong>

          <small>
            +{formatPrice(targetThreeReward)}
          </small>
        </div>
      </div>

      <div className="tradePlanFooter">
  <div>
    <span>Risk / Reward</span>

    <strong>
      {tradeBlocked
        ? "BLOCKED"
        : `1 : ${riskReward.toFixed(1)}`}
    </strong>
  </div>
</div>
        </>
      )}
  
    </section>
  );
}

function ReviewFact({ label, value }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function formatReviewNumber(value) {
  return typeof value === "number" ? `$${value.toFixed(4)}` : "—";
}
function formatPrice(price) {
  if (typeof price !== "number") {
    return "—";
  }

  return `$${price.toFixed(2)}`;
}

export default NinaTradePlan;
